// @mui
import { Box, Container, Grid } from '@mui/material'
import { styled } from '@mui/material/styles'
import uniqBy from 'lodash/uniqBy'
import flatten from 'lodash/flatten'
// routes
import { PATH_PAGE } from '@/routes/paths'
// hooks
import useSettings from '@/hooks/useSettings'
// layouts
import Layout from '@/layouts/index'
// @types
import { Merchandise, MerchandisePreview } from '@/types/merchandise'
// components
import Page from '@/components/Page'
import { SkeletonProduct } from '@/components/skeleton'
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs'
// sections
import { productService } from '@/services/product'
import { settingService, GlobalSetting, SeoMetadata } from '@/services/page'
import pathService from '@/services/path'
import { notEmpty } from '@/utils/common'
import useApp from '@/hooks/useApp'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { APPS } from '@/appConfig'
import type { ParsedUrlQuery } from 'querystring'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import {
  ProductDetailsCarousel,
  ProductDetailsSummary,
  ProductDetailsContent,
  ProductDetailsSimilar,
} from '@/sections/e-commerce/product-details'

interface PathProps extends ParsedUrlQuery {
  appId: string
  slug: string[]
}

interface ProductPageProps {
  product: Merchandise | null
  moreProducts: MerchandisePreview[]
  globalSetting?: GlobalSetting
  seoMetadataItemsCollection?: SeoMetadata[]
  callToActionLink: {
    title: string
    link: string
  } | null
}
// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(8),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(11),
  },
}))

// ----------------------------------------------------------------------

Product.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

// ----------------------------------------------------------------------

export default function Product({
  product = null,
  moreProducts = [],
  seoMetadataItemsCollection = [],
  callToActionLink = null,
}: ProductPageProps) {
  const { logo, host } = useApp()
  const { locale } = useRouter()
  const { themeStretch } = useSettings()

  return (
    <Page
      title={product?.title ?? ''}
      description={
        product?.content?.json
          ? documentToPlainTextString(product.content?.json).slice(250)
          : undefined
      }
      metadataItems={seoMetadataItemsCollection}
      openGraphImage={{
        url: product?.previewUrl ?? product?.images[0] ?? '',
        alt: product?.title ?? '',
      }}
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product?.title,
        url: `https://${host}/${locale}/product/${product?.slug}`,
        image: (product?.previewUrl || product?.images[0] || logo) ?? '',
        productionDate: product?.publishedAt ?? '',
        description: product?.content?.json
          ? documentToPlainTextString(product.content?.json).slice(250)
          : undefined,
      }}
    >
      <RootStyle>
        <Container maxWidth={themeStretch ? false : 'lg'} sx={{ pb: 2 }}>
          <HeaderBreadcrumbs
            links={[
              { name: 'Products', href: PATH_PAGE.products },
              { name: product?.title ?? '' },
            ].filter(notEmpty)}
          />

          {product && (
            <>
              <Grid container>
                <Grid item xs={12} md={6}>
                  <ProductDetailsCarousel product={product} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ProductDetailsSummary product={product} callToActionLink={callToActionLink} />
                </Grid>
              </Grid>
              <Box mt={5}>
                <ProductDetailsContent content={product.content} />
              </Box>
            </>
          )}

          {!product && <SkeletonProduct />}

          {Boolean(moreProducts.length) && <ProductDetailsSimilar products={moreProducts} />}
        </Container>
      </RootStyle>
    </Page>
  )
}

export const getStaticProps: GetStaticProps<ProductPageProps, PathProps> = async ({
  params,
  locales = [],
  locale,
}) => {
  if (!params) throw new Error('No path parameters found')
  const { appId } = params
  const { getProductIdByProductSlug, getProductAndMoreProductsById } = productService(appId)
  const { getGlobalSetting } = settingService(appId)
  const globalSetting = await getGlobalSetting({ locale })

  let seoMetadataItemsCollection: SeoMetadata[] = []
  let callToActionLink: {
    title: string
    link: string
  } | null = null

  let productId: string | null = null
  let product: Merchandise | null = null
  let moreProducts: MerchandisePreview[] = []

  productId = await getProductIdByProductSlug(params.slug.join('/'), { locale })

  if (productId) {
    const data = await getProductAndMoreProductsById(productId, true, { locale })
    product = data.product
    moreProducts = data.moreProducts
    seoMetadataItemsCollection = data.seoMetadataItemsCollection
    callToActionLink = data.callToActionLink ?? null
  }
  return {
    props: {
      product,
      moreProducts,
      seoMetadataItemsCollection,
      globalSetting,
      callToActionLink,
    },
    revalidate: 3600,
  }
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getProductPaths } = pathService()

  const allAppProductPaths = await Promise.allSettled(
    APPS.map(async (app) => getProductPaths(app.id))
  ).then((res) => res.map((r) => (r.status === 'fulfilled' ? r.value : null)).filter(notEmpty))

  const allProductPaths = uniqBy(
    flatten(allAppProductPaths).filter(({ params: { slug } }) => slug.join('')),
    (item) => item.params.slug.join('') + item.params.appId + item.locale
  )

  return {
    paths: allProductPaths,
    fallback: true,
  }
}
